import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";
import useMonetization from "../../../../hooks/useMonetization";
import useCreditsBalance from "../../../../hooks/useCreditsBalance";
import { useUserPersonalInfo } from "../../../../hooks/useUserPersonalInfo";
import subscriptionService from "../../../../services/subscriptionService";
import SubscriptionPlanCard from "../../../elements/SubscriptionPlanCard";
import SubscriptionBenefits from "../../../elements/SubscriptionBenefits";
import { showToast } from "../../../../utils/toast";
import { useAppSelector } from "../../../../redux/typedHooks";
import CoinIcon from "../../../../assets/svgs/CoinIcon";
import CCBillPayment from "../../../elements/CCBillPayment";
import { getAllTokens } from "../../../../api/ccbillApi";
import creditPackagesService from "../../../../api/creditPackagesApi";
import { invalidateCreditsQuery } from "../../../../query";
import CreditPackageCard from "../../../elements/CreditPackageCard";
import { trackOfferViewed } from "../../../../analytics/canonicalTracking";
import calendarIcon from "../../../../assets/images/calendar.png";
import storeIcon from "../../../../assets/images/store.png";
import discountIcon from "../../../../assets/images/discount.png";
import onyxLogo from "../../../../assets/badgets/onyx.png";
import sapphireLogo from "../../../../assets/badgets/shapfire.png";
import diamondLogo from "../../../../assets/badgets/diamand.png";
import platinumLogo from "../../../../assets/badgets/platium.png";
import freemiumLogo from "../../../../assets/badgets/free.png";

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  costPerCredit: number;
  description: string;
  targetSegment: string;
  popular: boolean;
  sortOrder: number;
  currency: string;
  totalValue: number;
  effectiveCostPerCredit: number;
  discountPercentage: number;
}

interface SubscriptionPlan {
  tier: "freemium" | "onyx" | "sapphire" | "diamond" | "platinum_vip";
  name: string;
  price: number;
  credits: number;
  perks: string[];
  popular?: boolean;
}

interface PackagesResponse {
  success: boolean;
  data: {
    packages: CreditPackage[];
    currency: string;
    totalPackages: number;
  };
}

const useStyles = makeStyles(theme => ({
  pageContainer: {
    minHeight: "100vh",
    // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "2rem 0",
    [theme.breakpoints.down("sm")]: {
      padding: "1rem 0",
    },
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1rem",
  },
  pageHeader: {
    textAlign: "center",
    marginBottom: "3rem",
    color: "white",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "2rem",
    },
  },
  pageTitle: {
    fontSize: "3rem",
    fontWeight: 700,
    color: "#FFFFFF",
    marginBottom: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "110%",
    },
  },
  pageSubtitle: {
    fontSize: "1.2rem",
    color: "#FFFFFF",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  },
  subscriptionSection: {
    display: "flex",
    gap: "1.5rem",
    marginBottom: "3rem",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  subscriptionLeftBlock: {
    flex: 1,
    backgroundColor: "#131313",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  subscriptionRightBlock: {
    maxWidth: "380px",
    width: "100%",
    backgroundColor: "#131313",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    [theme.breakpoints.down("md")]: {
      maxWidth: "100%",
    },
  },
  subscriptionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  subscriptionContentWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  perksGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  subscriptionPrice: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#747474",
    [theme.breakpoints.down("sm")]: {
      fontSize: "28px",
    },
  },
  subscriptionCreditsInfo: {
    fontSize: "16px",
    color: "#fff",
    display: "flex",
    gap: 4,
    alignItems: "center",
  },
  upgradeButton: {
    backgroundColor: "transparent",
    border: "1px solid #FE5A97",
    color: "#FFF",
    borderRadius: "8px",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    maxWidth: 250,
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.1)",
      borderColor: "#FE5A97",
    },
  },
  creditsLabel: {
    fontSize: "14px",
    color: "#FFFFFFB2",
    fontWeight: 500,
    letterSpacing: "1px",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  creditsValue: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#FFFFFF",
    marginBottom: "16px",
  },
  creditsRenewal: {
    fontSize: "20px",
    color: "#FFFFFF",
    marginBottom: "16px",
  },
  buyCreditsButton: {
    backgroundColor: "#F1A21F",
    height: "48px",
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 700,
    borderRadius: "8px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d8921c",
    },
  },
  section: {
    marginBottom: "3rem",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "2rem",
    },
  },
  sectionTitle: {
    fontSize: "2rem",
    fontWeight: 600,
    color: "white",
    marginBottom: "2rem",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
      marginBottom: "1.5rem",
    },
  },
  plansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(273px, 1fr))",
    gap: "1.5rem",
    marginTop: "2rem",
    "& > *": {
      width: "100%",
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      "& > *": {
        width: "100%",
      },
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  errorContainer: {
    margin: "2rem 0",
  },
  emptyState: {
    textAlign: "center",
    color: "white",
    padding: "3rem 1rem",
  },
  emptyStateTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1rem",
  },
  emptyStateText: {
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    maxWidth: "400px",
    margin: "0 auto",
  },
  gapPerDay: {
    marginLeft: 125,
  },
  pageContainerPA: {
    minHeight: "100vh",
    backgroundColor: "#0f0f0f",
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  containerPA: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  pageHeaderPA: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(3),
    },
  },
  pageTitlePA: {
    color: "white",
    fontSize: "48px",
    fontWeight: 800,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      fontSize: "32px",
    },
    // background: "linear-gradient(135deg, #FE5A97 0%, #ff6b9d 100%)",
    // WebkitBackgroundClip: "text",
    // WebkitTextFillColor: "transparent",
    // backgroundClip: "text",
  },
  pageSubtitlePA: {
    color: "#ccc",
    fontSize: "18px",
    lineHeight: 1.6,
    maxWidth: "600px",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  loadingContainerPA: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  errorContainerPA: {
    textAlign: "center",
    color: "#ff4444",
    fontSize: "18px",
    padding: theme.spacing(4),
    backgroundColor: "#2a1a1a",
    borderRadius: "12px",
    border: "1px solid #ff4444",
  },
  packagesGrid: {
    marginTop: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionTitlePA: {
    color: "white",
    fontSize: "24px",
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  benefitsSection: {
    marginTop: theme.spacing(6),
    padding: theme.spacing(4),
  },
  benefitsTitle: {
    color: "white",
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  benefitsList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  benefitItem: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#fff",
    fontSize: "18px",
    fontWeight: 600,
    borderRadius: 16,
    padding: 30,
    backgroundColor: "#131313",
  },
  benefitIcon: {
    color: "#FE5A97",
    "& svg": {
      width: "43px",
      height: "43px",
    },
  },
}));

const getTierLogo = (tier: string) => {
  switch (tier) {
    case "freemium":
      return freemiumLogo;
    case "onyx":
      return onyxLogo;
    case "sapphire":
      return sapphireLogo;
    case "diamond":
      return diamondLogo;
    case "platinum_vip":
      return platinumLogo;
    default:
      return freemiumLogo;
  }
};

const SubscriptionsPage: React.FC = () => {
  const seo = useSEO("subscriptions");
  const classes = useStyles();
  const history = useHistory();

  const {
    subscription,
    loading: monetizationLoading,
    refetch: refetchMonetization,
  } = useMonetization();

  const { totalCredits } = useCreditsBalance();

  // Add user personal info hook for refreshing user data
  const { onRefetch: onRefetchUserPersonalInfo } = useUserPersonalInfo();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [showBenefits, setShowBenefits] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [packages, setPackages] = useState<CreditPackage[]>([]);

  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(
    null
  );
  const [subscriptionPaymentOpen, setSubscriptionPaymentOpen] =
    useState(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState<SubscriptionPlan | null>(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [hasActiveCard, setHasActiveCard] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [upgradingTier, setUpgradingTier] = useState<string | null>(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const plansRef = useRef<HTMLDivElement | null>(null);
  const packagesRef = useRef<HTMLDivElement | null>(null);

  const currentUser = useAppSelector((state: any) => state.user.currentUser);
  const currentUserId = currentUser?.id;

  const user = useAppSelector<{
    id: string;
  } | null>(state => state.user.currentUser);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const subscriptionOfferPageTracked = useRef(false);
  useEffect(() => {
    if (subscriptionOfferPageTracked.current) return;
    subscriptionOfferPageTracked.current = true;
    const role = currentUser?.userType?.title?.toLowerCase?.() || "guest";
    trackOfferViewed({
      offer_type: "subscription",
      user_id: currentUserId,
      user_role:
        role === "viewer" || role === "creator" || role === "model"
          ? role
          : "guest",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update timer every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchPackages();

    // Check for CCBill redirect return with successful payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get("paymentSuccess");
    const packageId =
      urlParams.get("packageId") || localStorage.getItem("pendingPackageId");

    if (paymentSuccess === "true" && packageId) {
      // Clean up URL and localStorage
      localStorage.removeItem("pendingPackageId");
      window.history.replaceState({}, document.title, window.location.pathname);

      // Wait for packages to load, then complete purchase
      setTimeout(() => {
        handlePurchaseCompletion(packageId);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurchaseCompletion = async (packageId: string) => {
    try {
      setPurchasing(packageId);
      const response = await creditPackagesService.purchasePackage(
        packageId,
        "ccbill"
      );

      if (response?.success) {
        const packageData = packages.find(p => p.id === packageId);
        showToast(
          `Successfully purchased ${packageData?.credits || ""} credits!`,
          "success"
        );
        await fetchPackages();
        await invalidateCreditsQuery();
      } else {
        showToast(response?.message || "Failed to complete purchase", "error");
      }
    } catch (error: any) {
      console.error("Error completing purchase:", error);
      showToast(
        error?.response?.data?.message ||
        error?.message ||
        "Error completing purchase",
        "error"
      );
    } finally {
      setPurchasing(null);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await subscriptionService.getPlans();
      if (response.success) {
        // Відображаємо лише потрібні плани
        const allowedTiers = ["onyx", "sapphire", "diamond", "platinum_vip"];
        const filteredPlans = (response.data.plans || []).filter(plan =>
          allowedTiers.includes(plan.tier)
        );

        // Нормалізуємо/перевизначаємо дані для Sapphire (ціна, кредити, перелік бенефітів)
        const normalizedPlans = filteredPlans.map(plan =>
          plan.tier === "sapphire"
            ? {
              ...plan,
              name: "Sapphire",
              price: 49.99,
              credits: 180,
              perks: [
                "Sapphire badge in profile",
                "Priority DM delivery",
                "Advanced search",
                "1 daily DM boost",
              ],
            }
            : plan
        );

        setPlans(normalizedPlans);
      } else {
        showToast("Error loading subscription plans", "error");
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      showToast("Error loading subscription plans", "error");
    } finally {
      setPlansLoading(false);
    }
  };

  const handleSubscribe = async (tier: string) => {
    // Free план обробляємо без CCBill
    if (tier === "freemium") {
      showToast("Switched to free plan!", "success");
      onRefetchUserPersonalInfo();
      return;
    }

    // Знаходимо план, щоб знати ціну/назву
    const selectedPlan = plans.find(plan => plan.tier === tier);
    if (!selectedPlan) {
      showToast("Plan not found", "error");
      return;
    }

    // Якщо вже є активна підписка – використовуємо існуючу логіку оновлення/даунгрейду
    if (
      subscription &&
      subscription.tier !== tier &&
      subscription.status === "active"
    ) {
      try {
        setUpgradingTier(tier);
        const response = await subscriptionService.changeSubscription(
          tier,
          "ccbill"
        );

        if (response.success) {
          const currentPlan = plans.find(
            plan => plan.tier === subscription.tier
          );
          const isUpgrade =
            currentPlan && selectedPlan.price > currentPlan.price;

          showToast(
            (response as any).message ||
            `Successfully ${isUpgrade ? "upgraded" : "downgraded"
            } to ${tier} plan!`,
            "success"
          );
          await Promise.all([
            invalidateCreditsQuery(),
            onRefetchUserPersonalInfo(),
          ]);
        } else {
          showToast(
            (response as any).message || "Error changing subscription",
            "error"
          );
        }
      } catch (error: any) {
        console.error("Error changing subscription:", error);
        showToast(
          error?.response?.data?.message || "Failed to change subscription",
          "error"
        );
      } finally {
        setUpgradingTier(null);
      }
      return;
    }

    // Нові платні підписки – відкриваємо CCBill флоу (редірект з компонента CCBillPayment)
    setSelectedSubscriptionPlan(selectedPlan);
    setSubscriptionPaymentOpen(true);
  };

  const handleUpgrade = async (newTier: string) => {
    try {
      // Find the plan to verify it exists
      const selectedPlan = plans.find(plan => plan.tier === newTier);
      if (!selectedPlan) {
        showToast("Plan not found", "error");
        return;
      }

      setUpgradingTier(newTier);
      // Use universal change endpoint - automatically determines upgrade/downgrade
      const response = await subscriptionService.changeSubscription(
        newTier,
        "ccbill"
      );

      if (response.success) {
        // Determine action type from response message or plan prices for display
        const currentPlan = subscription
          ? plans.find(plan => plan.tier === subscription.tier)
          : null;
        const isUpgrade = currentPlan && selectedPlan.price > currentPlan.price;

        showToast(
          (response as any).message ||
          `Subscription successfully ${isUpgrade ? "upgraded" : "downgraded"
          }!`,
          "success"
        );
        // Refresh both monetization and user personal info
        await Promise.all([invalidateCreditsQuery(), onRefetchUserPersonalInfo()]);
      } else {
        showToast(
          (response as any).message || "Error updating subscription",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      showToast(
        (error as any)?.response?.data?.message ||
        "Error updating subscription",
        "error"
      );
    } finally {
      setUpgradingTier(null);
    }
  };

  const formatTimeRemaining = (expiresAt: string): string => {
    const expiryDate = new Date(expiresAt);
    const now = currentTime;
    const diff = expiryDate.getTime() - now.getTime();

    if (diff <= 0) {
      return "Expired";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  const handlePurchase = async (packageId: string) => {
    try {
      setPurchasing(packageId);

      // Check if user has active CCBill card (token)
      let activeToken = false;
      try {
        const tokens: any = await getAllTokens();
        activeToken = !!tokens?.cards?.find((t: any) => t.isEnabled);
        setHasActiveCard(activeToken);
      } catch (_) {
        activeToken = false;
        setHasActiveCard(false);
      }

      // Always show CCBill payment dialog first
      // After successful payment, we'll call the purchase API
      const target = packages.find(p => p.id === packageId) || null;
      if (target) {
        // Store packageId in localStorage in case of redirect flow
        localStorage.setItem("pendingPackageId", packageId);
        setSelectedPackage(target);
        setPaymentOpen(true);
      } else {
        showToast("Package not found", "error");
      }
    } catch (error) {
      console.error("Error purchasing package:", error);
      showToast("Error purchasing package", "error");
    } finally {
      setPurchasing(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      const response = await subscriptionService.cancelSubscription(user?.id);

      if (response.success) {
        showToast("Subscription cancelled successfully!", "success");
        // Refresh both monetization and user personal info
        await Promise.all([invalidateCreditsQuery(), onRefetchUserPersonalInfo()]);
      } else {
        showToast(
          (response as any).message || "Error cancelling subscription",
          "error"
        );
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      showToast(
        (error as any)?.response?.data?.message ||
        "Error cancelling subscription",
        "error"
      );
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response: PackagesResponse =
        await creditPackagesService.getPackages();

      if (response.success) {
        setPackages(response.data.packages);
      } else {
        showToast("Failed to load credit packages", "error");
      }
    } catch (error) {
      console.error("Error fetching credit packages:", error);
      showToast("Error loading credit packages", "error");
    } finally {
      setLoading(false);
    }
  };

  const isLoading = monetizationLoading || plansLoading;
  const currentTier =
    subscription && subscription.status === "active"
      ? subscription.tier
      : "freemium";

  return (
    <>
      <StandardPageHelmet seo={seo} />

      <Box className={classes.pageContainer}>
        <Box className={classes.container}>
          <Box className={classes.pageHeader}>
            <Typography className={classes.pageTitle}>
              Subscription plans
            </Typography>
            <Typography className={classes.pageSubtitle}>
              Choose the best plan that fits your needs
            </Typography>
          </Box>

          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={60} style={{ color: "#FE5A97" }} />
            </Box>
          )}

          {!isLoading && plans.length === 0 && (
            <Box className={classes.errorContainer}>
              Failed to load subscription plans. Please try again later.
            </Box>
          )}

          {!isLoading && plans.length > 0 && (
            <>
              {/* Subscription and Credits Section */}
              <Box className={classes.subscriptionSection}>
                {/* Left Block - Active Subscription */}
                <Box className={classes.subscriptionLeftBlock}>
                  {subscription &&
                    subscription.status === "active" &&
                    subscription.tier !== "freemium" ? (
                    <>
                      {(() => {
                        const currentPlan = plans.find(
                          p => p.tier === subscription.tier
                        );
                        const dailyCredits = currentPlan?.credits || 0;
                        return (
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {/* Top row - Plan name and price */}
                            <Box
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "32px",
                                width: "100%",
                              }}
                            >
                              <Box
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  flex: 1,
                                  gap: "12px",
                                }}
                              >
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                  }}
                                >
                                  {currentPlan?.tier && (
                                    <img
                                      src={getTierLogo(currentPlan.tier)}
                                      alt={currentPlan?.name}
                                      style={{ width: 32, height: 32 }}
                                    />
                                  )}
                                  <Typography
                                    style={{ color: "white" }}
                                    className={classes.subscriptionPrice}
                                  >
                                    {currentPlan?.name}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="baseline">
                                  <Typography
                                    style={{ color: "white" }}
                                    className={classes.subscriptionPrice}
                                  >
                                    ${currentPlan?.price || 0}
                                  </Typography>
                                  <Typography
                                    className={`${classes.gapPerDay} ${classes.subscriptionCreditsInfo}`}
                                  >
                                    <CoinIcon /> <b> {dailyCredits}</b>{" "}
                                    credits/months
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            {/* Bottom row - Auto renewal text, button and perks */}
                            <Box className={classes.subscriptionContentWrapper}>
                              <Box
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "12px",
                                }}
                              >
                                <Typography
                                  style={{
                                    fontSize: "14px",
                                    color: "#FFFFFFB2",
                                  }}
                                >
                                  Auto renewal, cancel anytime
                                </Typography>
                                <Button
                                  className={classes.upgradeButton}
                                  onClick={() => {
                                    setShowManageModal(true);
                                  }}
                                  style={{
                                    alignSelf: "flex-start",
                                  }}
                                >
                                  Manage my plan
                                </Button>
                              </Box>
                              {/* Right side - Features list */}
                              {currentPlan?.perks &&
                                (Array.isArray(currentPlan.perks)
                                  ? currentPlan.perks.length > 0 && (
                                    <Box className={classes.perksGrid}>
                                      {currentPlan.perks.map(
                                        (perk, index) => (
                                          <Box
                                            key={index}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                            }}
                                          >
                                            <Typography
                                              style={{
                                                color: "#fff",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                marginTop: "2px",
                                              }}
                                            >
                                              ✓
                                            </Typography>
                                            <Typography
                                              style={{
                                                color: "#fff",
                                                fontSize: "14px",
                                                lineHeight: 1.4,
                                              }}
                                            >
                                              {perk}
                                            </Typography>
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                  )
                                  : Object.entries(currentPlan.perks)
                                    .map(([key, value]) => {
                                      if (!value) return null;

                                      switch (key) {
                                        case "badge":
                                          return `${value} badge`;
                                        case "priorityDelivery":
                                          return "Priority DM delivery";
                                        case "advancedSearch":
                                          return "Advanced Search";
                                        case "dailyBoosts":
                                          return `${value} daily DM boost${Number(value) > 1 ? "s" : ""
                                            }`;
                                        case "profileHighlight":
                                          return "Profile Highlight";
                                        case "conciergeMatching":
                                          return "Concierge Matching";
                                        default:
                                          return null;
                                      }
                                    })
                                    .filter(Boolean).length > 0 && (
                                    <Box className={classes.perksGrid}>
                                      {Object.entries(currentPlan.perks)
                                        .map(([key, value]) => {
                                          if (!value) return null;

                                          switch (key) {
                                            case "badge":
                                              return `${value} badge`;
                                            case "priorityDelivery":
                                              return "Priority DM delivery";
                                            case "advancedSearch":
                                              return "Advanced Search";
                                            case "dailyBoosts":
                                              return `${value} daily DM boost${Number(value) > 1 ? "s" : ""
                                                }`;
                                            case "profileHighlight":
                                              return "Profile Highlight";
                                            case "conciergeMatching":
                                              return "Concierge Matching";
                                            default:
                                              return null;
                                          }
                                        })
                                        .filter(Boolean)
                                        .map((perk, index) => (
                                          <Box
                                            key={index}
                                            style={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                              gap: "8px",
                                            }}
                                          >
                                            <Typography
                                              style={{
                                                color: "#fff",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                marginTop: "2px",
                                              }}
                                            >
                                              ✓
                                            </Typography>
                                            <Typography
                                              style={{
                                                color: "#fff",
                                                fontSize: "14px",
                                                lineHeight: 1.4,
                                              }}
                                            >
                                              {perk}
                                            </Typography>
                                          </Box>
                                        ))}
                                    </Box>
                                  ))}
                            </Box>
                          </Box>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      <Box className={classes.subscriptionInfo}>
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <img
                            src={freemiumLogo}
                            alt="Freemium"
                            style={{ width: 32, height: 32 }}
                          />
                          <Typography
                            style={{ color: "white" }}
                            className={classes.subscriptionPrice}
                          >
                            Freemium
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="baseline">
                          <Typography
                            style={{ color: "#747474" }}
                            className={classes.subscriptionPrice}
                          >
                            $0.00
                          </Typography>
                          <Typography
                            className={`${classes.gapPerDay} ${classes.subscriptionCreditsInfo}`}
                          >
                            <CoinIcon /> <b> 2</b> credits/day
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        className={classes.upgradeButton}
                        onClick={() => {
                          if (plansRef.current) {
                            plansRef.current.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          } else {
                            setShowBenefits(true);
                          }
                        }}
                      >
                        Upgrade my plan
                      </Button>
                    </>
                  )}
                </Box>

                {/* Right Block - Credits Balance */}
                <Box className={classes.subscriptionRightBlock}>
                  <Box>
                    <Typography className={classes.creditsLabel}>
                      Credits Balance:
                    </Typography>
                    <Typography className={classes.creditsValue}>
                      {totalCredits}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.creditsLabel}>
                      Credits Renewal:
                    </Typography>
                    {subscription && subscription.status === "active" ? (
                      <Typography className={classes.creditsRenewal}>
                        {subscription.expiresAt
                          ? formatTimeRemaining(subscription.expiresAt)
                          : "Auto-renewal enabled"}
                      </Typography>
                    ) : (
                      <Typography className={classes.creditsRenewal}>
                        No active subscription
                      </Typography>
                    )}
                  </Box>
                  <Button
                    className={classes.buyCreditsButton}
                    onClick={() => {
                      if (packagesRef.current) {
                        packagesRef.current.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                    fullWidth
                  >
                    Buy more Credits
                  </Button>
                </Box>
              </Box>

              {/* Плани підписок */}
              <Box className={classes.section} id="plans">
                <Typography className={classes.sectionTitle}>
                  Upgrade your plan
                </Typography>
                <div ref={plansRef}>
                  <Box className={classes.plansGrid}>
                    {plans &&
                      plans?.length > 0 &&
                      plans
                        .filter(
                          plan =>
                            !(
                              subscription?.status === "active" &&
                              subscription?.tier === plan.tier
                            )
                        )
                        .map(plan => {
                          // Знаходимо поточний план для порівняння (для визначення тексту кнопки)
                          const currentPlanForComparison =
                            subscription?.status === "active" &&
                              subscription?.tier
                              ? plans.find(p => p.tier === subscription.tier)
                              : null;

                          return (
                            <Box key={plan.tier}>
                              <SubscriptionPlanCard
                                isActive={
                                  subscription?.status === "active" &&
                                  subscription?.tier === plan.tier
                                }
                                plan={plan}
                                currentTier={currentTier}
                                currentPlan={currentPlanForComparison}
                                onSubscribe={handleSubscribe}
                                onUpgrade={handleUpgrade}
                                discountPercentage={10}
                                loading={upgradingTier === plan.tier}
                              />
                            </Box>
                          );
                        })}
                  </Box>
                </div>
              </Box>

              {/* Показати повідомлення про оновлення для freemium користувачів */}
              {(!subscription ||
                subscription.tier === "freemium" ||
                subscription.status === "none") && (
                  <Box className={classes.emptyState}>
                    <Typography className={classes.emptyStateTitle}>
                      Upgrade to Premium Plan
                    </Typography>
                    <Typography className={classes.emptyStateText}>
                      Get more features and capabilities with our premium
                      subscription plans.
                    </Typography>
                  </Box>
                )}
            </>
          )}

          {/* Packages Grid */}
          {!loading && packages.length > 0 && (
            <div ref={packagesRef} id="packages">
              <Typography className={classes.sectionTitle}>
                Available Packages
              </Typography>

              <Box className={classes.packagesGrid}>
                {packages
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map(packageData => (
                    <Box key={packageData.id}>
                      <CreditPackageCard
                        package={packageData}
                        onPurchase={handlePurchase}
                        loading={purchasing === packageData.id}
                      />
                    </Box>
                  ))}
              </Box>

              {/* Benefits Section */}
              <Box className={classes.benefitsSection}>
                <Typography className={classes.benefitsTitle}>
                  Why Choose Our Credit Packages?
                </Typography>

                <Box className={classes.benefitsList}>
                  <Box className={classes.benefitItem}>
                    <Box className={classes.benefitIcon}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </Box>
                    Instant credit delivery
                  </Box>

                  <Box className={classes.benefitItem}>
                    <Box className={classes.benefitIcon}>
                      <img src={discountIcon} alt="discountIcon" />
                    </Box>
                    Better value with larger packages
                  </Box>

                  <Box className={classes.benefitItem}>
                    <Box className={classes.benefitIcon}>
                      <img src={storeIcon} alt="storeIcon" />
                    </Box>
                    Secure payment processing
                  </Box>

                  <Box className={classes.benefitItem}>
                    <Box className={classes.benefitIcon}>
                      <img src={calendarIcon} alt="calendarIcon" />
                    </Box>
                    No expiration date
                  </Box>
                </Box>
              </Box>
            </div>
          )}
          {/* CCBill Payment Dialog */}
          <Dialog
            open={paymentOpen}
            onClose={() => {
              setPaymentOpen(false);
              // Clean up localStorage if dialog is closed without purchase
              localStorage.removeItem("pendingPackageId");
            }}
          >
            <Box style={{ background: "#1a1a1a" }}>
              <CCBillPayment
                title="Secure Credit Purchase"
                buttonText="Pay with CCBill"
                description={
                  selectedPackage
                    ? `Buy ${selectedPackage.credits
                    } credits for $${selectedPackage.price.toFixed(2)}`
                    : undefined
                }
                price={selectedPackage?.price}
                context="post-purchase"
                userId={currentUserId}
                showPrice
                onProceed={() => {
                  /* no-op */
                }}
                onPaymentSuccess={async () => {
                  // After successful CCBill payment, call purchase API to finalize the purchase
                  if (selectedPackage) {
                    try {
                      setPurchasing(selectedPackage.id);
                      const response =
                        await creditPackagesService.purchasePackage(
                          selectedPackage.id,
                          "ccbill"
                        );

                      if (response?.success) {
                        showToast(
                          `Successfully purchased ${selectedPackage.credits} credits!`,
                          "success"
                        );
                        // Refresh packages list to update any changes
                        await fetchPackages();
                        await invalidateCreditsQuery();
                        // Clean up localStorage
                        localStorage.removeItem("pendingPackageId");
                        setPaymentOpen(false);
                        setSelectedPackage(null);
                      } else {
                        showToast(
                          response?.message || "Failed to complete purchase",
                          "error"
                        );
                      }
                    } catch (error: any) {
                      console.error("Error completing purchase:", error);
                      showToast(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Error completing purchase",
                        "error"
                      );
                    } finally {
                      setPurchasing(null);
                    }
                  }
                }}
              />
              {!hasActiveCard && (
                <Typography
                  style={{
                    color: "#bbb",
                    fontSize: 12,
                    textAlign: "center",
                    padding: 12,
                  }}
                >
                  After adding your card with CCBill, return here and tap
                  Purchase again.
                </Typography>
              )}
            </Box>
          </Dialog>

          {/* Subscription CCBill Payment Dialog */}
          <Dialog
            open={subscriptionPaymentOpen}
            onClose={() => {
              setSubscriptionPaymentOpen(false);
              setSelectedSubscriptionPlan(null);
            }}
          >
            <Box style={{ background: "#1a1a1a" }}>
              <CCBillPayment
                title="Secure Subscription Payment"
                buttonText="Pay with CCBill"
                description={
                  selectedSubscriptionPlan
                    ? `Subscribe to ${selectedSubscriptionPlan.name} for $${selectedSubscriptionPlan.price.toFixed(
                      2
                    )} per month`
                    : undefined
                }
                price={selectedSubscriptionPlan?.price}
                context="subscription"
                userId={currentUserId}
                subscriptionToUserId={currentUserId}
                showPrice
                onProceed={() => {
                  /* редірект виконується всередині CCBillPayment */
                }}
              />
            </Box>
          </Dialog>

          {/* Manage subscription modal */}
          <Dialog
            open={showManageModal}
            onClose={() => setShowManageModal(false)}
          >
            <Box
              style={{
                background: "#1a1a1a",
                color: "#fff",
                padding: 24,
                minWidth: 320,
                maxWidth: 420,
              }}
            >
              <Typography variant="h6" style={{ marginBottom: 12 }}>
                Manage subscription
              </Typography>
              <Typography style={{ marginBottom: 16, color: "#ccc" }}>
                Current plan: {subscription?.tier || "freemium"}
              </Typography>
              <Typography style={{ marginBottom: 24, color: "#ccc" }}>
                You can cancel your active subscription. Your access will remain
                until the end of the current billing period.
              </Typography>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
                <Button
                  onClick={() => setShowManageModal(false)}
                  style={{ color: "#fff" }}
                >
                  Close
                </Button>
                <Button
                  onClick={async () => {
                    await handleCancelSubscription();
                    setShowManageModal(false);
                  }}
                  disabled={cancellingSubscription}
                  style={{
                    color: "#fff",
                    backgroundColor: "#FE5A97",
                  }}
                >
                  {cancellingSubscription ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <CircularProgress size={20} style={{ color: "#fff" }} />
                      Cancelling...
                    </Box>
                  ) : (
                    "Cancel subscription"
                  )}
                </Button>
              </Box>
            </Box>
          </Dialog>

          <SubscriptionBenefits
            isOpen={showBenefits}
            onClose={() => setShowBenefits(false)}
          />
        </Box>
      </Box>
    </>
  );
};

export default SubscriptionsPage;
